import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { usePageDocumentDetails } from "./GeoLocationLevel.hooks";

export const AddEditGeoLocationLevels = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = usePageDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    const activeTab = query.get("activeTab");
    if (activeTab === "pageHeading") {
      refetchCategories();
    }
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("activeTab"), query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      long_description: editPageContentById
        ? decodeHTML(editPageContentById.payload?.long_description)
        : editPageContent
          ? decodeHTML(editPageContent?.long_description)
          : "",

      long_description_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.long_description_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.long_description_malay)
          : "",

      long_description_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.long_description_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.long_description_mandarin)
          : "",

      terms_conditions: editPageContentById
        ? decodeHTML(editPageContentById.payload?.terms_conditions)
        : editPageContent
          ? decodeHTML(editPageContent?.terms_conditions)
          : "",

      terms_conditions_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.terms_conditions_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.terms_conditions_malay)
          : "",

      terms_conditions_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.terms_conditions_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.terms_conditions_mandarin)
          : "",

      technical_id: editPageContentById
        ? decodeHTML(editPageContentById.payload?.technical_id)
        : editPageContent
          ? decodeHTML(editPageContent?.technical_id)
          : "",

      document_url: editPageContentById
        ? decodeHTML(editPageContentById.payload.document_url)
        : editPageContent
          ? decodeHTML(editPageContent?.document_url)
          : "",
      refer_document_text: editPageContentById
        ? decodeHTML(editPageContentById.payload.refer_document_text)
        : editPageContent
          ? decodeHTML(editPageContent?.refer_document_text)
          : "",

      redirect_url: editPageContentById
        ? decodeHTML(editPageContentById.payload.redirect_url)
        : editPageContent
          ? decodeHTML(editPageContent?.redirect_url)
          : "",

      // project_airport_id: editPageContentById
      //   ? decodeHTML(editPageContentById.payload.project_airport_id)
      //   : editPageContent
      //     ? decodeHTML(editPageContent?.project_airport_id)
      //     : "",

      parent_id_promo: editPageContentById
        ? decodeHTML(editPageContentById.payload.parent_id_promo)
        : editPageContent
          ? decodeHTML(editPageContent?.parent_id_promo)
          : null,

      levels: editPageContentById
        ? decodeHTML(editPageContentById.payload.levels)
        : editPageContent
          ? decodeHTML(editPageContent?.levels)
          : null,

      published: editPageContentById
        ? decodeHTML(editPageContentById.payload?.published)
        : editPageContent
          ? decodeHTML(editPageContent?.published)
          : "",
    },

    fields: fields.geoLocationPromotionLevels,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image", "banner_image", "square_image", "logo"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CENTRALIZED");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    let descriptionKeys: any = [
      "long_description",
      "long_description_malay",
      "long_description_mandarin",
      "terms_condition",
      "terms_condition_malay",
      "terms_condition_mandarin",
    ];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }

    const payload = {
      action,
      method,
      module: "Centralized Promotions",
      subModule: "geoLocationPromotionLevels",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes,
      appList: [{ appName: "CENTRALIZED", apiUrl }],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
        }),
        ...data,
      },
      status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Geo Location Promotion Levels`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${centralizedHost}/api/geo-location-promotion-levels/${editPageContent?.id || editPageContentById?.payload?.id}` : `${centralizedHost}/api/geo-location-promotion-levels`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this MyAirports Promotions"
      />
    </ContentWrapper>
  );
};
